/**
 * Importaciones — listado de embarques.
 *
 * Cada fila es un contenedor/orden al suplidor con su pipeline
 * (negociación → fabricación → tránsito → puerto → liquidación → cerrado).
 * Los semáforos salen de las fechas de la cabecera: fabricación agotándose
 * (fechaanticipo + días de fabricación), saldo del suplidor por vencer
 * (ETA-20 con pagos pendientes) y manifestación de chasis (ETA-5). El
 * detalle abre en un Dialog fullScreen (embarque.js). El catálogo de
 * navieras/transportistas se mantiene aquí mismo.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { fmtMoney, toNum } from "lib/moneda";
import Embarque, { chipEstado } from "modules/importaciones/embarque";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const ESTADOS_FILTRO = [
  { value: "", label: "Todos" },
  { value: "negociacion", label: "Negociación" },
  { value: "fabricacion", label: "Fabricación" },
  { value: "transito", label: "En tránsito" },
  { value: "puerto", label: "En puerto" },
  { value: "liquidacion", label: "Liquidado" },
  { value: "cerrado", label: "Cerrado" },
  { value: "cancelado", label: "Cancelado" },
];

const filtrarSuplidores = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.id} ${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

/** 'dd/mm/aaaa' → días desde hoy (negativo = ya pasó). */
const diasHasta = (v) => {
  const p = String(v || "").split("/");
  if (p.length !== 3) return null;
  const fecha = new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return Math.round((fecha - hoy) / 86400000);
};

/**
 * Semáforo del embarque a partir de sus fechas (espejo de la hoja del
 * cliente): fabricación agotándose, saldo a ETA-20 y manifestación a ETA-5.
 */
export function alertaEmbarque(e) {
  if (e.estado === "fabricacion" && e.fechaanticipo) {
    const transcurridos = -diasHasta(e.fechaanticipo);
    const restantes = (e.diasfabricacion || 45) - transcurridos;
    if (restantes < 0) return { label: `Fabricación vencida (${-restantes}d)`, nivel: "rojo" };
    if (restantes <= 7) return { label: `Fabricación: quedan ${restantes}d`, nivel: "ambar" };
  }
  if (e.estado === "transito" && e.fechaeta) {
    const dias = diasHasta(e.fechaeta);
    if (dias != null) {
      if (dias <= 5 && !e.fechamanifestacion) {
        return { label: "Manifestar chasis (ETA-5)", nivel: "rojo" };
      }
      if (dias <= 20 && e.pagos_pendientes > 0) {
        return { label: `Saldo por vencer (ETA en ${dias}d)`, nivel: "ambar" };
      }
    }
  }
  if (e.estado === "puerto") {
    return { label: "Liquidación pendiente", nivel: "ambar" };
  }
  if (e.estado === "liquidacion") {
    return { label: "Listo para recibir", nivel: "verde" };
  }
  return null;
}

const chipAlerta = (a) => {
  if (!a) return null;
  const colores = {
    rojo: { bg: "#FFEBEE", fg: "#C62828" },
    ambar: { bg: "#FFF3E0", fg: "#E65100" },
    verde: { bg: "#E8F5E9", fg: "#2E7D32" },
  }[a.nivel];
  return (
    <Chip
      size="small"
      label={a.label}
      sx={{ bgcolor: colores.bg, color: colores.fg, fontWeight: 700 }}
    />
  );
};

export default function Embarques() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  // La baja del catálogo exige el flag borrar (permisos.py); sin él el
  // botón no se muestra en vez de devolver 403.
  const puedeBorrar = puedeAccionPrograma(controller.roleDetalles, "importaciones", "borrar");

  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [detalleId, setDetalleId] = useState(null);

  // Llegada desde el widget del Inicio (/importaciones/embarques?id=N): abre
  // el expediente y limpia el parámetro para que cerrar no lo reabra.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = Number(searchParams.get("id"));
    if (id > 0) {
      setDetalleId(id);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Crear
  const [dialogCrear, setDialogCrear] = useState(false);
  const [creando, setCreando] = useState(false);
  const [suplidores, setSuplidores] = useState([]);
  const [nuevo, setNuevo] = useState({});

  // Catálogo
  const [dialogCatalogo, setDialogCatalogo] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [catTipo, setCatTipo] = useState("naviera");
  const [catNombre, setCatNombre] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/importaciones_listar", { estado: filtroEstado });
      if (ok && Array.isArray(data.embarques)) {
        setRows(data.embarques);
      } else {
        showAlert({ message: data.Error || "No se pudieron cargar los embarques", color: "error" });
      }
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, filtroEstado]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    let activo = true;
    post("/api/cxp_suplidores", {}).then(({ ok, data }) => {
      if (activo && ok) setSuplidores(data.suplidores || []);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarCatalogo = useCallback(async () => {
    const { ok, data } = await post("/api/importaciones_catalogo_listar", { todos: true });
    if (ok) setCatalogo(data.catalogo || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  // La proforma se transcribe después en el expediente; el arranque de la
  // negociación exige suplidor, FOB, % de anticipo y días de fabricación
  // (el backend re-valida).
  const nuevoValido =
    Boolean(nuevo.suplidor?.id) &&
    toNum(nuevo.montofob) > 0 &&
    toNum(nuevo.porcentajeanticipo) > 0 &&
    toNum(nuevo.porcentajeanticipo) <= 100 &&
    toNum(nuevo.diasfabricacion ?? 45) > 0;

  const crear = async () => {
    setCreando(true);
    try {
      const { ok, data } = await post("/api/importaciones_crear", {
        suplidor_id: nuevo.suplidor?.id || null,
        suplidornombre: nuevo.suplidor?.descrip || "",
        moneda: nuevo.moneda || "U",
        montofob: nuevo.montofob || "",
        porcentajeanticipo: nuevo.porcentajeanticipo || "",
        diasfabricacion: nuevo.diasfabricacion || 45,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Embarque creado", color: "success" });
        setDialogCrear(false);
        setNuevo({});
        cargar();
        if (data.id) setDetalleId(data.id);
      } else {
        showAlert({ message: data.Error || "No se pudo crear el embarque", color: "error" });
      }
    } finally {
      setCreando(false);
    }
  };

  const columnas = [
    { field: "id", headerName: "#", width: 70 },
    { field: "suplidornombre", headerName: "Suplidor", flex: 1.1, minWidth: 160 },
    { field: "proforma", headerName: "Proforma", width: 110 },
    {
      field: "naviera",
      headerName: "Naviera / BL",
      flex: 0.9,
      minWidth: 130,
      renderCell: (p) => [p.row.naviera, p.row.bl].filter(Boolean).join(" · ") || "—",
    },
    {
      field: "chasis_total",
      headerName: "Chasis",
      width: 90,
      renderCell: (p) => `${p.row.chasis_recibidos}/${p.row.chasis_total}`,
    },
    {
      field: "montofob",
      headerName: "FOB",
      width: 120,
      renderCell: (p) =>
        p.row.montofob != null
          ? `${p.row.moneda === "U" ? "US$" : "RD$"} ${fmtMoney(p.row.montofob)}`
          : "—",
    },
    { field: "fechaeta", headerName: "ETA", width: 100 },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      renderCell: (p) => chipEstado(p.row.estado),
    },
    {
      field: "alerta",
      headerName: "Alerta",
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderCell: (p) => chipAlerta(alertaEmbarque(p.row)),
    },
    {
      field: "abrir",
      headerName: "",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Button
          size="small"
          onClick={() => setDetalleId(p.row.id)}
          sx={{ ...btnBase, color: FP.color.navy }}
        >
          Abrir
        </Button>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Importaciones"
        subtitle="Embarques de motocicletas: negociación, tránsito, liquidación y entrada a inventario"
        cta={
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<SettingsOutlinedIcon />}
              onClick={() => {
                cargarCatalogo();
                setDialogCatalogo(true);
              }}
              sx={btnOutline(FP.color.navy)}
            >
              Catálogos
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setDialogCrear(true)}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Nuevo embarque
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              sx={fpInputSx}
            >
              {ESTADOS_FILTRO.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={9} md={10} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              startIcon={
                cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
              }
              onClick={cargar}
              disabled={cargando}
              sx={btnOutline(FP.color.navy)}
            >
              Actualizar
            </Button>
          </Grid>
        </Grid>

        <DataGrid
          autoHeight
          rows={rows}
          columns={columnas}
          loading={cargando}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={fpDataGridSx}
        />
      </Box>

      {/* Crear embarque */}
      <Dialog open={dialogCrear} onClose={() => setDialogCrear(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Nuevo embarque</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            <Grid item xs={12}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                filterOptions={filtrarSuplidores}
                options={suplidores}
                value={nuevo.suplidor || null}
                getOptionLabel={(o) => (typeof o === "string" ? o : `${o.id} — ${o.descrip || ""}`)}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => setNuevo((n) => ({ ...n, suplidor: v }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Suplidor"
                    sx={fpInputSx}
                    helperText={
                      suplidores.length === 0
                        ? "Sin suplidores registrados (se administran en Cuentas x Pagar)"
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                select
                fullWidth
                required
                label="Moneda"
                value={nuevo.moneda || "U"}
                onChange={(e) => setNuevo((n) => ({ ...n, moneda: e.target.value }))}
                sx={fpInputSx}
              >
                <MenuItem value="U">USD</MenuItem>
                <MenuItem value="P">RD$</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                required
                label="Monto FOB"
                value={nuevo.montofob || ""}
                onChange={(e) => setNuevo((n) => ({ ...n, montofob: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                required
                label="% anticipo"
                value={nuevo.porcentajeanticipo || ""}
                onChange={(e) => setNuevo((n) => ({ ...n, porcentajeanticipo: e.target.value }))}
                sx={fpInputSx}
                helperText="Acordado, puede variar"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                required
                label="Días fabricación"
                value={nuevo.diasfabricacion ?? "45"}
                onChange={(e) => setNuevo((n) => ({ ...n, diasfabricacion: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCrear(false)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
          <Button
            onClick={crear}
            disabled={creando || !nuevoValido}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            {creando ? <CircularProgress size={16} color="inherit" /> : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Catálogos */}
      <Dialog
        open={dialogCatalogo}
        onClose={() => setDialogCatalogo(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Navieras y transportistas</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.2, mb: 1.5 }}>
            <Grid item xs={5}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={catTipo}
                onChange={(e) => setCatTipo(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="naviera">Naviera</MenuItem>
                <MenuItem value="transportista">Transportista</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Nombre"
                value={catNombre}
                onChange={(e) => setCatNombre(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                fullWidth
                disabled={!catNombre.trim()}
                onClick={async () => {
                  const { ok, data } = await post("/api/importaciones_catalogo_guardar", {
                    catalogo_id: 0,
                    tipo: catTipo,
                    nombre: catNombre.trim(),
                  });
                  if (ok) {
                    setCatNombre("");
                    cargarCatalogo();
                  } else {
                    showAlert({ message: data.Error || "No se pudo guardar", color: "error" });
                  }
                }}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </Button>
            </Grid>
          </Grid>
          {catalogo
            .filter((c) => c.tipo === catTipo)
            .map((c) => (
              <Stack
                key={c.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ borderBottom: `1px solid ${FP.color.border}`, py: 0.4 }}
              >
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: c.activo === "S" ? FP.color.textPrimary : FP.color.textMuted,
                    textDecoration: c.activo === "S" ? "none" : "line-through",
                  }}
                >
                  {c.nombre}
                </Typography>
                {c.activo === "S" && puedeBorrar && (
                  <Button
                    size="small"
                    sx={{ ...btnBase, color: FP.color.danger, minWidth: 36 }}
                    onClick={async () => {
                      const { ok, data } = await post("/api/importaciones_catalogo_quitar", {
                        catalogo_id: c.id,
                      });
                      if (ok) cargarCatalogo();
                      else
                        showAlert({
                          message: data.Error || "No se pudo desactivar",
                          color: "error",
                        });
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </Button>
                )}
              </Stack>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCatalogo(false)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle */}
      <Dialog fullScreen open={detalleId != null} onClose={() => setDetalleId(null)}>
        {detalleId != null && (
          <Embarque id={detalleId} onExit={() => setDetalleId(null)} onChanged={cargar} />
        )}
      </Dialog>
    </Box>
  );
}
